import { useState, useEffect, useCallback } from 'react';
import api from '../../api';
import { urlEliminazione, messaggioEliminazione, avvisaConflitto } from '../../utils/eliminazione';
import Layout from '../../components/Layout';
import stili from '../../components/competenze/stili';
import ConflittoVersione, { eConflitto } from '../../components/ConflittoVersione';
import { usePuoScrivere } from '../../utils/permessi';

const CAMPI = {
  tipologia_contrattuale: 'Tipologia contrattuale',
  causa_cessazione: 'Causa di cessazione',
  trasformazione: 'Trasformazione',
  qualifica: 'Qualifica professionale (ISTAT)',
};

// Tabella dei codici UNILAV: associa codice + descrizione, così come sono scritti nei PDF, al significato usato dall'app.
// Un codice o una descrizione non presenti qui non vengono mai interpretati: la riga resta "da verificare".
const ORIGINI = { confermato_da_marco: 'confermato da Marco', inserito_da_utente: 'inserito da utente', appreso_da_file: 'appreso da file' };

export default function CodiciUnilav() {
  const puoScrivere = usePuoScrivere('scadenziario');
  const [dati, setDati] = useState({ codici: [], significati: {} });
  const [inModifica, setInModifica] = useState(null);
  const [errore, setErrore] = useState('');

  const carica = useCallback(() => {
    api.get('/scadenze/unilav/codici').then(r => setDati(r.data)).catch(err => setErrore(err.response?.data?.errore || 'Errore nel caricamento'));
  }, []);
  useEffect(() => { carica(); }, [carica]);

  const elimina = async (c) => {
    if (!window.confirm(`Spostare nel Cestino il codice ${c.codice}? I PDF con questo codice resteranno "da verificare".`)) return;
    try { await api.delete(urlEliminazione(`/scadenze/unilav/codici/${c.id}`, c.version)); carica(); } catch (err) { setErrore(avvisaConflitto(err, messaggioEliminazione(err))); if (eConflitto(err)) carica(); }
  };

  return (
    <Layout>
      <div style={stili.topBar}>
        <h2 style={{ margin: 0 }}>Codici UNILAV</h2>
        {puoScrivere && <button onClick={() => setInModifica({ voce: null })} style={stili.btn}>+ Nuovo codice</button>}
      </div>
      <p style={{ color: '#555', fontSize: '0.88rem', marginTop: 0 }}>
        Un codice si riconosce solo se codice e descrizione coincidono con quelli del PDF. Per una descrizione diversa dello stesso codice aggiungi una riga.
      </p>
      {errore && <div style={stili.errore}>{errore}</div>}
      {Object.entries(CAMPI).map(([campo, nome]) => (
        <div key={campo} style={{ marginBottom: '1.2rem' }}>
          <h3 style={{ marginBottom: '0.4rem' }}>{nome}</h3>
          <div style={{ overflowX: 'auto' }}>
          <table style={stili.table}>
            <thead><tr><th style={stili.th}>Codice</th><th style={stili.th}>Descrizione (come nel PDF)</th><th style={stili.th}>Significato</th><th style={stili.th}>Origine</th>{puoScrivere && <th style={stili.th}>Azioni</th>}</tr></thead>
            <tbody>
              {dati.codici.filter(c => c.campo === campo).map(c => (
                <tr key={c.id} style={stili.tr}>
                  <td style={stili.td}>{c.codice}</td>
                  <td style={stili.td}>{c.descrizione}</td>
                  <td style={stili.td}>{dati.significati[campo]?.[c.significato] || c.significato}</td>
                  <td style={stili.td}>{ORIGINI[c.origine] || 'già in tabella'}</td>
                  {puoScrivere && (
                    <td style={stili.td}>
                      <button onClick={() => setInModifica({ voce: c })} style={stili.btnSmall}>Modifica</button>
                      <button onClick={() => elimina(c)} style={stili.btnSmallDanger}>Elimina</button>
                    </td>
                  )}
                </tr>
              ))}
              {!dati.codici.some(c => c.campo === campo) && <tr><td colSpan={5} style={{ ...stili.td, color: '#999' }}>Nessun codice</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      ))}
      {inModifica && <FormCodice voce={inModifica.voce} significati={dati.significati} onChiudi={() => setInModifica(null)} onSalvato={() => { setInModifica(null); carica(); }} />}
    </Layout>
  );
}

function FormCodice({ voce, significati, onChiudi, onSalvato }) {
  const daVoce = v => ({ campo: v?.campo || 'tipologia_contrattuale', codice: v?.codice || '', descrizione: v?.descrizione || '', significato: v?.significato || '', version: v?.version });
  const [form, setForm] = useState(daVoce(voce));
  const [errore, setErrore] = useState('');
  const [conflitto, setConflitto] = useState(null);
  const opzioni = Object.entries(significati[form.campo] || {});

  const salva = async (version) => {
    setErrore('');
    try {
      if (voce) await api.put(`/scadenze/unilav/codici/${voce.id}`, { ...form, version });
      else await api.post('/scadenze/unilav/codici', form);
      onSalvato();
    } catch (err) {
      if (eConflitto(err)) setConflitto(err.response.data);
      else setErrore(err.response?.data?.errore || 'Errore nel salvataggio');
    }
  };

  return (
    <div style={stili.overlay}>
      <div style={{ ...stili.modal, maxWidth: '520px' }}>
        <h3 style={{ marginTop: 0 }}>{voce ? 'Modifica codice' : 'Nuovo codice'}</h3>
        <form onSubmit={e => { e.preventDefault(); salva(form.version); }}>
          {errore && <div style={stili.errore}>{errore}</div>}
          <div style={stili.campo}>
            <label style={stili.label}>Campo</label>
            <select value={form.campo} disabled={Boolean(voce)} onChange={e => setForm(f => ({ ...f, campo: e.target.value, significato: e.target.value === 'qualifica' ? 'qualifica' : '' }))} style={stili.input}>
              {Object.entries(CAMPI).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div style={stili.campo}>
            <label style={stili.label}>Codice (come nel PDF)</label>
            <input value={form.codice} onChange={e => setForm(f => ({ ...f, codice: e.target.value }))} required style={stili.input} />
          </div>
          <div style={stili.campo}>
            <label style={stili.label}>Descrizione (come nel PDF)</label>
            <input value={form.descrizione} onChange={e => setForm(f => ({ ...f, descrizione: e.target.value }))} required style={stili.input} />
          </div>
          {form.campo !== 'qualifica' && (
          <div style={stili.campo}>
            <label style={stili.label}>Significato</label>
            <select value={form.significato} onChange={e => setForm(f => ({ ...f, significato: e.target.value }))} required style={stili.input}>
              <option value="">Scegli…</option>
              {opzioni.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          )}
          <div style={stili.azioni}>
            <button type="button" onClick={onChiudi} style={stili.btnSecondary}>Annulla</button>
            <button type="submit" style={stili.btn}>Salva</button>
          </div>
        </form>
      </div>
      {conflitto && <ConflittoVersione conflitto={conflitto} onRicarica={() => { setForm(daVoce(conflitto.attuale)); setConflitto(null); }} onSovrascrivi={() => salva(conflitto.attuale.version)} />}
    </div>
  );
}
